import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItemDivider,
  IonLabel,
  IonList,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react'
import { chevronBackOutline, close } from 'ionicons/icons'
import { CameraResultType, Camera, CameraSource, CameraDirection, Photo } from '@capacitor/camera'
import '@capacitor/camera'
import { useHistory } from 'react-router-dom'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetCameraAttachmentsState,
  saveImage,
  setCategory,
  setImage,
  setName,
} from '../store/cameraAttachments.actions'
import SInput from '../../../components/stringInput/StringInput.component'
import { getCameraAttachmentsSelector } from '../selectors'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../common/common.types'
import ImageComponent from '../../../components/image/Image.component'
import './CameraAttachments.page.css'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { getCameraAttachmentsOptionsSelector } from '../../options/selectors'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { AuthenticationService } from '../../login/services/authentication.service'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { getUserSelector } from '../../user/selectors'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { Capacitor } from '@capacitor/core'
import Logger from '../../../services/logger.service'

const CameraAttachments: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [photo, setPhoto] = useState<Photo | undefined>(undefined)
  const user = useSelector(getUserSelector)
  const cameraAttachmentsState = useSelector(getCameraAttachmentsSelector)
  const { name, category } = cameraAttachmentsState
  const cameraAttachmentsOptionsState = useSelector(getCameraAttachmentsOptionsSelector)
  const { categoryOptions } = cameraAttachmentsOptionsState
  const [isFocused, setIsFocused] = useState(false)
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const { PR_REQ_CHART_ATTACH_CATEGORY }: IServiceConfig = useSelector(getServiceConfigSelector)
  const [isCameraOpen, setIsCameraOpen] = useState(false)
  const isActiveRef = useRef<boolean>(false)

  useIonViewDidEnter(() => {
    isActiveRef.current = true
    checkCameraPermission()
  })

  useIonViewWillLeave(() => {
    isActiveRef.current = false
    setPhoto(undefined)
    setIsCameraOpen(false)

    if (isPwaContext()) {
      document.querySelector('pwa-camera-modal-instance')?.remove()
      document.querySelector('pwa-camera-modal')?.remove()
    }
  })

  useEffect(() => {
    if (!isCameraOpen || !isPwaContext()) return

    const hideButton = () => {
      const modal = document.querySelector('pwa-camera-modal-instance')
      const pwaCamera = modal?.shadowRoot?.querySelector('pwa-camera')
      const button = pwaCamera?.shadowRoot?.querySelector('.pick-image') as HTMLElement
      if (button) button.style.display = 'none'
    }

    const hideButtonTimeoutId = window.setTimeout(hideButton, 100)

    const observer = new MutationObserver(hideButton)
    const observeTimeoutId = window.setTimeout(() => {
      const modal = document.querySelector('pwa-camera-modal-instance')
      const pwaCamera = modal?.shadowRoot?.querySelector('pwa-camera')
      if (pwaCamera?.shadowRoot) {
        observer.observe(pwaCamera.shadowRoot, { childList: true, subtree: true })
      }
    }, 200)

    return () => {
      window.clearTimeout(hideButtonTimeoutId)
      window.clearTimeout(observeTimeoutId)
      observer.disconnect()
    }
  }, [isCameraOpen])

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  const handleBack = () => {
    dispatch(resetCameraAttachmentsState())
    history.push(TabConstants.ATTACHMENT_TAB)
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const alertState = {
      header: 'Warning',
      message: 'You have not saved this attachment data, are you sure you want to change pages and clear this data?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: handleBack }],
    }
    dispatch(showAlert(alertState))
  }

  const checkCameraPermission = async () => {
    if (isPwaContext()) {
      await checkPwaPermissions()
    } else {
      takePhoto()
    }
  }
  const isPwaContext = (): boolean => {
    return !Capacitor.isNativePlatform()
  }

  const checkPwaPermissions = async () => {
    try {
      const permissionState = await navigator.permissions.query({ name: 'camera' as PermissionName })

      if (permissionState.state === 'granted') {
        takePhoto()
      } else if (permissionState.state === 'prompt') {
        await requestCameraAccess()
      } else {
        Logger.error('Camera permissions denied in PWA context')
      }
    } catch (error) {
      Logger.error('Error checking PWA camera permissions:' + error)
    }
  }

  const requestCameraAccess = async () => {
    let stream: MediaStream | undefined
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true })
      if (stream) {
        takePhoto()
      }
    } catch (error) {
      Logger.error('User denied camera access:' + error)
    } finally {
      stream?.getTracks().forEach((track) => track.stop())
    }
  }

  const takePhoto = async () => {
    setIsCameraOpen(true)
    await Camera.getPhoto({
      quality: 60,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      saveToGallery: false,
      source: CameraSource.Camera,
      presentationStyle: 'fullscreen',
      correctOrientation: true,
      width: 2048,
      height: 1536,
      direction: CameraDirection.Rear,
    })
      .then((photo) => {
        if (!isActiveRef.current) return
        dispatch(setImage(photo))
        setPhoto(photo)
      })
      .catch((error) => {
        if (!isActiveRef.current) return
        Logger.error(error.message)
        dispatch(resetCameraAttachmentsState())
        history.replace(TabConstants.ATTACHMENT_TAB)
      })
      .finally(() => {
        if (!isActiveRef.current) return
        setIsCameraOpen(false)
      })
  }

  const onClear = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(resetCameraAttachmentsState())
    setPhoto(undefined)
    takePhoto()
  }

  const saveOnClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (PR_REQ_CHART_ATTACH_CATEGORY === 1 && !category) {
      const alertState = {
        header: 'Warning',
        message: `${t('attachments.CategoryRequiredMsg')}`,
      }
      dispatch(showAlert(alertState))
      return
    }
    dispatch(saveImage())
    history.replace(TabConstants.ATTACHMENT_TAB)
  }

  const setNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setName(e.detail.value))
  }

  const setCategoryOnChange = (option: IOption) => {
    dispatch(setCategory(option))
  }

  return (
    <IonPage id='CameraAttachmentsPage' data-testid='CameraAttachments'>
      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            {!isCameraOpen && (
              <IonButton data-testid='Back' color='primary-alt' onClick={onBackClick}>
                <IonIcon icon={chevronBackOutline} />
                {t('common.buttons.Back')}
              </IonButton>
            )}
          </IonButtons>
          <IonButtons slot='end'>
            {!isCameraOpen && (
              <IonButton data-testid='Save' color='primary-alt' fill='clear' onClick={saveOnClick}>
                {t('common.buttons.Save')}
              </IonButton>
            )}
          </IonButtons>
          <IonTitle>{t('cameraAttachments.cameraAttachments')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className='bg-lt-grey' color='light'>
        {photo && (
          <>
            <IonList>
              <IonGrid className='no-padding'>
                <IonRow>
                  <IonCol className='camera-attachment-col'>
                    <ImageComponent
                      isPHI
                      src={`data:image/${photo.format};base64,${photo.base64String}`}
                      alt={t('cameraAttachments.imagePreview')}
                      className='camera-image'
                    />
                    <IonButton data-testid='Clear' fill='clear' onClick={onClear}>
                      <IonIcon icon={close} className='custom-close-icon' slot='icon-only' />
                    </IonButton>
                  </IonCol>
                </IonRow>

                <IonItemDivider color='light' className='ion-padding'>
                  <IonLabel className='grid-item-divider-label' color='medium'>
                    {t('cameraAttachments.addDetails')}
                  </IonLabel>
                </IonItemDivider>

                <IonRow>
                  <IonCol className='picklist'>
                    <SInput
                      label={t('cameraAttachments.name')}
                      dataTestID='ImageName'
                      inputValue={name}
                      onChangeAction={setNameOnChange}
                      setIsFocused={setIsFocused}
                      maxlength={250}
                    />
                  </IonCol>
                </IonRow>

                <hr className='field-divider' />

                <IonRow>
                  <IonCol className='picklist'>
                    <SingleSelect
                      dataTestID='ImageCategory'
                      options={categoryOptions}
                      selectedOption={category}
                      listHeader={t('cameraAttachments.category')}
                      label={t('cameraAttachments.category')}
                      action={setCategoryOnChange}
                    />
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonList>
          </>
        )}

        {isFocused && (
          <IonItemDivider
            color='light'
            className={isTabletDevice ? 'bottom-divider-tablet' : 'bottom-divider-mobile'}
          />
        )}
      </IonContent>
    </IonPage>
  )
}

export default CameraAttachments
